<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <!--table hauptkonfigurationen -->
                <v-data-table
                    v-model:sort-by="sortBy"
                    v-model:expanded="expanded"
                    :headers="headers"
                    :items="benutzerAsList"
                    :loading="loading"
                    :custom-sort="customSort"
                    :must-sort="true"
                    item-value="objectBenutzerId"
                    show-expand
                    single-expand
                    hide-default-footer
                    class="elevation-1 nowhow-table"
                    :sort-by="['email']"
                    :items-per-page="-1"
                    @click:row="expandRow"
                >
                    <!-- Header der Table(Filter anzeige, gefundene Elemente) -->
                    <template #top>
                        <TableHeader>
                            <template #default>
                                <v-container fluid>
                                    <v-row>
                                        <v-col cols="12" md="5">
                                            <v-text-field
                                                v-model="filterData.userSignInEmail"
                                                prepend-inner-icon="mdi-magnify"
                                                append-icon="mdi-close"
                                                :label="$t('suche')"
                                                hide-details
                                                autofocus
                                                @keyup.enter="searchEnter"
                                                @click:append="clearSearch"
                                            ></v-text-field>
                                        </v-col>

                                        <v-col cols="12" md="3">
                                            <v-btn class="mt-2" color="primary" variant="flat" @click="searchEnter">
                                                {{ $t('benutzerSuche.search') }}
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-container>
                            </template>
                        </TableHeader>
                    </template>
                    <!-- In der Spalte "TVS-Nr" soll bei einem Tierarzt Benutzer wie gehabt die TVS Nummer des Tierarztes
                     angezeigt werden, bei einem Lieferanten die TVS Lieferantennummer. Bei allen anderen bleibt die Spalte leer -->
                    <template #item.kundenNummer="{ item }">
                        <template v-if="item.lieferantenNummer">
                            {{ item.lieferantenNummer }}
                        </template>
                        <template v-else-if="item.kundenNummer">
                            {{ item.kundenNummer }}
                        </template>
                    </template>
                    <template #item.firma="{ item }">
                        {{ item.firma }}
                    </template>
                    <template #item.plz="{ item }">
                        <template v-if="item.plz !== 0">
                            {{ item.plz }}
                        </template>
                    </template>
                    <template #item.ort="{ item }">
                        {{ item.ort }}
                    </template>
                    <template #item.userSignInEmail="{ item }">
                        {{ item.userSignInEmail }}
                    </template>
                    <template #item.burgermenu="{ item }">
                        <v-btn icon :href="item.burgermenu" flat alt="test" @click="initTakeSpecific(item.teilnehmerId, item.objectBenutzerId)">
                            <v-icon color="primary" icon="mdi-account-cog" />
                        </v-btn>
                    </template>
                    <!-- Override default expand button -->
                    <template #item.data-table-expand="{ item }">
                        <v-btn icon class="elevation-0">
                            <v-icon>{{ isRowExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                        </v-btn>
                    </template>

                    <template #expanded-row="{}">
                        <tr class="bg-surface-light py-1">
                            <td class="d-none d-md-table-cell"></td>
                            <td colspan="100" class="px-4 py-2">
                                <v-row class="d-flex flex-wrap">
                                    <v-col cols="12" md="6" class="mb-2">
                                        <span>{{ $t('einstellungen.benutzer.rollen') + ' ' }}</span>
                                        <span v-if="benutzerDetails && benutzerDetails.rollen">
                                            {{ $t('benutzerrollen.' + benutzerDetails.rollen.toString().toLowerCase()) }}
                                        </span>
                                        <span v-else>-</span>
                                    </v-col>

                                    <v-col cols="12" md="6" class="mb-2">
                                        <span>{{ $t('einstellungen.benutzer.mobile') + ' ' }}</span>
                                        <span v-if="benutzerDetails && benutzerDetails.mobilNummer">
                                            {{ benutzerDetails.mobilNummer }}
                                        </span>
                                        <span v-else>-</span>
                                    </v-col>
                                </v-row>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';

import { NAME as NAME_GENERAL } from '@/store/modules/general';
import { NAME as NAME_SECURITY } from '@/store/modules/security';
import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
import { NAME as NAME_TAKETEILNEHMER } from '../store';

import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';
import { HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED } from '@/store/modules/security/mutations';
import { NEW_ALERT } from '@/store/modules/headerNotification/actions';
import { BenutzerInfoSearchResultApiDto, TakeTeilnehmerApiDto } from '@/api-schema';
import { GET_BENUTZER, GET_BENUTZER_DETAILS, TAKESPECIFIC } from '../store/actions';
import { GET_BENUTZER_DATA, GET_BENUTZER_LOADING, GET_BENUTZER_TOTAL_NUMBER_OF_RECORDS, GETBENUTZERDETAILS } from '../store/getters';
import { BenutzerInfoSearchApiDto } from '@/types/BenutzerInfoSearchApiDto';
import { TakeTeilnehmerDto, TeilnehmerBenutzerDto } from '@/types/UserDtos';
import { APPLY_BENUTZERFILTER, CLEAR_BENUTZERFILTER } from '../store/mutations';

const generalModul = namespace(NAME_GENERAL);
const securityModul = namespace(NAME_SECURITY);
const headerModul = namespace(NAME_HEADERNOTIFICATION);
const administrationModul = namespace(NAME_TAKETEILNEHMER);

import TableHeader from '@/components/TableHeader.vue';
@Component({
    components: {
        TableHeader
    }
})
export default class BenutzerSuche extends Vue {
    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;

    @headerModul.Action(NEW_ALERT) alertMessage!: Function;

    @administrationModul.Getter(GET_BENUTZER_DATA)
    benutzerList: Array<BenutzerInfoSearchResultApiDto>;
    @administrationModul.Getter(GET_BENUTZER_LOADING) loading!: boolean;
    @administrationModul.Action(GET_BENUTZER) getBenutzer!: Function;
    @administrationModul.Action(TAKESPECIFIC) takeSpecific!: Function;
    @administrationModul.Getter(GET_BENUTZER_TOTAL_NUMBER_OF_RECORDS)
    totalNumberOfRecords!: number;

    @administrationModul.Mutation(CLEAR_BENUTZERFILTER) clearFilter!: Function;
    @administrationModul.Mutation(APPLY_BENUTZERFILTER) applyFilters!: Function;
    @administrationModul.Action(GET_BENUTZER_DETAILS) getBenutzerDetails!: Function;
    @administrationModul.Getter(GETBENUTZERDETAILS) benutzerDetails!: any;

    filters = [];
    showFilterDrawer = false;

    sortBy: [{ key: 'teilnehmerId'; order: 'desc' }];
    expanded: Array<BenutzerInfoSearchResultApiDto> = [];
    filterData: BenutzerInfoSearchApiDto = new BenutzerInfoSearchApiDto();

    private async initTakeSpecific(teilnehmerId: number, objectBenutzerId: string) {
        try {
            const benutzerDetails = await this.getBenutzerDetails(objectBenutzerId);

            // If roles exist and it's an array, take the first entry, otherwise null
            const rolle =
                benutzerDetails?.rollen && Array.isArray(benutzerDetails.rollen) && benutzerDetails.rollen.length > 0 ? benutzerDetails.rollen[0] : null;

            const teilnehmer: TakeTeilnehmerApiDto = new TakeTeilnehmerDto(rolle, teilnehmerId);
            this.takeSpecific(teilnehmer);
        } catch (error) {
            const teilnehmer: TakeTeilnehmerApiDto = new TakeTeilnehmerDto(null, teilnehmerId);
            this.takeSpecific(teilnehmer);
        }
    }

    get benutzerAsList(): Array<BenutzerInfoSearchResultApiDto> {
        return this.benutzerList || [];
    }

    get headers(): Array<any> {
        return [
            { title: '', key: 'data-table-expand', sortable: false }, // Expand button
            {
                title: this.$t('benutzerSuche.table.teilnehmerId'),
                key: 'kundenNummer'
            },
            {
                title: this.$t('benutzerSuche.table.firma'),
                key: 'firma'
            },
            {
                title: this.$t('benutzerSuche.table.plz'),
                key: 'plz'
            },
            {
                title: this.$t('benutzerSuche.table.ort'),
                key: 'ort'
            },
            {
                title: this.$t('benutzerSuche.table.email'),
                key: 'userSignInEmail'
            },
            {
                title: '',
                key: 'burgermenu',
                align: 'center',
                width: '10',
                sortable: false
            }
        ];
    }

    created() {
        this.clearSearch();
        this.setSubTitle(this.$t('benutzerSuche.subtitle'));
        this.setFooterTitle('S915');
        if (!this.hasAccess('Navigation.TakeTeilnehmer')) {
            this.setAccessDenied(true);
            this.alertMessage({ text: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
        }
    }

    private clearSearch() {
        this.filterData.userSignInEmail = '';
        this.applyFilters(this.filterData);
        this.clearFilter();
    }

    private removeFilter(value: string) {
        this.filters = this.filters.filter(function (obj) {
            return obj.key !== value;
        });

        this.applyFilters(this.filterData);
        this.getBenutzer();
    }

    private customSort(items: any[]) {
        return items;
    }

    private searchEnter() {
        this.applyFilters(this.filterData);
        this.getBenutzer();
    }

    private itemExpanded(teilnehmer: any) {
        this.getBenutzerDetails(teilnehmer.item.objectBenutzerId);
    }
    public expandRow(event: PointerEvent, row: any) {
        if (this.expanded.includes(row.item.objectBenutzerId)) {
            this.expanded = [];
            // Clear the details when collapsing
        } else {
            this.expanded = [row.item.objectBenutzerId];
            this.getBenutzerDetails(row.item.objectBenutzerId);
        }
    }

    isRowExpanded(row) {
        return this.expanded.includes(row.objectBenutzerId);
    }
}
</script>

<style scoped>
.v-application a {
    color: #0d47a1;
    cursor: pointer;
}
</style>
